import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { collection, onSnapshot } from 'firebase/firestore'
import {
  coachConsultationSchema,
  type CoachConsultation,
  type ReferralProgramOrBrandingId,
} from '@gh/common/models'
import { firestore } from '@/firebase'
import { useCustomerStore } from './customer'
import createLogger from '@/utils/logger'
import {
  getLatestCoachConsultationByBookedAt,
  hasCoachConsultationForProgram,
  hasFutureCoachConsultation,
} from '@gh/common/utils/coachConsultationBooking'

const logger = createLogger('stores/coachConsultations.ts')

export const useCoachConsultationsStore = defineStore('coachConsultations', () => {
  const customerStore = useCustomerStore()
  const consultations = ref<CoachConsultation[]>([])
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  let unsubscribeSnapshot: (() => void) | null = null

  const customerId = computed(() => customerStore.customerId)

  const latestConsultation = computed(() =>
    getLatestCoachConsultationByBookedAt(consultations.value),
  )

  const hasFutureConsultation = computed(() => hasFutureCoachConsultation(consultations.value))

  const hasConsultationForProgram = (referralProgramId: ReferralProgramOrBrandingId) =>
    hasCoachConsultationForProgram(consultations.value, referralProgramId)

  const stopSnapshot = () => {
    if (unsubscribeSnapshot) {
      unsubscribeSnapshot()
      unsubscribeSnapshot = null
    }
  }

  const subscribeToConsultations = (activeCustomerId: string) => {
    stopSnapshot()
    isLoading.value = true
    error.value = null

    unsubscribeSnapshot = onSnapshot(
      collection(firestore, 'customers', activeCustomerId, 'coach_consultations'),
      (snapshot) => {
        const nextConsultations: CoachConsultation[] = []
        snapshot.forEach((consultDoc) => {
          const parsed = coachConsultationSchema.safeParse({
            id: consultDoc.id,
            ...consultDoc.data(),
          })
          if (!parsed.success) {
            logger.error('Failed to parse coach consultation', parsed.error)
            return
          }
          nextConsultations.push(parsed.data)
        })
        consultations.value = nextConsultations
        isLoading.value = false
      },
      (snapshotError) => {
        logger.error('Failed to subscribe to coach consultations', snapshotError)
        error.value = 'Failed to load coach consultations'
        isLoading.value = false
      },
    )
  }

  watch(
    customerId,
    (activeCustomerId) => {
      stopSnapshot()
      consultations.value = []
      if (!activeCustomerId) {
        isLoading.value = false
        return
      }
      subscribeToConsultations(activeCustomerId)
    },
    { immediate: true },
  )

  return {
    consultations,
    isLoading,
    error,
    latestConsultation,
    hasFutureConsultation,
    hasConsultationForProgram,
    stopSnapshot,
  }
})
